import React from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useAuth } from '../../hooks/useAuth';
const farmersLogo = `${process.env.PUBLIC_URL}/Farmers_Insurance_Group_logo.svg`;

export default function BridgePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>('');

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const trackClickForGenerateQuote = params.get('trackClickForGenerateQuote');
    const policyid = params.get('policyid');
    const policy = decodePolicyParam(params.get('policy') ?? '');

    if (!token) {
      setError('Missing token in bridge URL.');
      setLoading(false);
      return;
    }

    validateNowcertsToken(token, trackClickForGenerateQuote ?? 'false', policyid ?? '', policy ?? '', login, navigate, setLoading, setError);
  }, [location.search, login, navigate]);

  const showError = !loading && !!error;

  const steps = [
    { label: 'Validating your credentials', icon: 'pi-lock' },
    { label: 'Setting up your workspace',   icon: 'pi-briefcase' },
    { label: 'Loading certificates',        icon: 'pi-file-check' },
  ];

  return (
    <div className="loginShell">
      <div className="loginShell__panel loginShell__panel--wide">
        <div className="loginShell__brand">
          <img src={farmersLogo} alt="Farmers" className="loginShell__brandLogo" />
        </div>

        <div className="loginShell__ssoBadge">
          <i className="pi pi-lock" style={{ fontSize: 11, marginRight: 5 }} />
          Nowcerts SSO
        </div>

        <div className="loginShell__headline" style={{ marginTop: 10 }}>
          {showError ? 'Unable to sign you in' : 'Welcome to the COI Portal'}
        </div>
        <p className="loginShell__subtext">
          {showError
            ? error
            : 'Securely connecting your Nowcerts account and preparing your workspace.'}
        </p>

        {loading && (
          <div className="loginShell__ssoLoader">
            <div className="loginShell__ssoSpinner">
              <span className="loginShell__ssoRing" />
            </div>
            <div className="loginShell__ssoSteps">
              {steps.map((s, i) => (
                <div key={i} className="loginShell__ssoStep loginShell__ssoStep--active" style={{ animationDelay: `${i * 0.5}s` }}>
                  <i className={`pi ${s.icon} loginShell__ssoStepIcon`} />
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {showError && (
          <>
            <p className="loginShell__hint">
              Close this window and relaunch from Nowcerts, or return to sign in.
            </p>
            <div className="loginShell__actions" style={{ marginTop: 16 }}>
              <Button
                type="button"
                label="Back to sign in"
                className="loginShell__primaryBtn"
                onClick={() => navigate('/', { replace: true })}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function validateNowcertsToken(
  token: string,
  trackClickForGenerateQuote: string,
  policyid: string,
  policy: string,
  login: (email: string) => void,
  navigate: (path: string, options?: { replace?: boolean }) => void,
  setLoading: (v: boolean) => void,
  setError: (msg: string) => void,
) {
  setLoading(true);

  const config = {
    method: 'get',
    url: `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/validateNowcertsToken?token=${encodeURIComponent(
      token,
    )}&trackClick=${encodeURIComponent(trackClickForGenerateQuote)}&policyid=${encodeURIComponent(policyid)}`,
  } as const;

  axios(config)
    .then((response: any) => {
      if (!response.data || !response.data.token) {
        clearAuthStorage();
        setError('Your session has expired or is invalid.');
        setLoading(false);
        return;
      }

      localStorage.setItem('SocialLogin', 'true');
      localStorage.setItem('igtoken', response.data.token);
      localStorage.setItem('tenantId', response.data.tenantId);
      localStorage.setItem('userName', response.data.userName);
      localStorage.setItem('RoleId', response.data.roleId);
      localStorage.setItem('NowcertsAgentId', response.data.nowCertsAgentId);
      localStorage.setItem('AvatarName', response.data.avatarName);
      localStorage.setItem('firstName', response.data.firstName ?? '');
      localStorage.setItem('lastName', response.data.lastName ?? '');
      localStorage.setItem('workEmail', response.data.workEmail ?? '');

      getNcToken(response.data.userName, response.data.token, policy, login, navigate, setLoading, setError);
    })
    .catch((error: any) => {
      setLoading(false);
      const status = error?.response?.status;

      if (status === 401) {
        clearAuthStorage();
        setError('Your session has expired. Please sign in again.');
      } else {
        setError('Unknown error while validating Nowcerts token.');
      }
    });
}

function getNcToken(
  ncSetupUserName: string,
  igToken: string,
  policy: string,
  login: (email: string) => void,
  navigate: (path: string, options?: { replace?: boolean }) => void,
  setLoading: (v: boolean) => void,
  setError: (msg: string) => void,
) {
  const data = JSON.stringify({
    NCUserName: ncSetupUserName,
  });

  const config = {
    method: 'post',
    url: `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/verifyMomentumCreds`,
    headers: {
      Authorization: `Bearer ${igToken}`,
      'Content-Type': 'application/json',
    },
    data,
  } as const;

  axios(config)
    .then((response: any) => {
      if (response.data) {
        localStorage.setItem('NCToken', response.data.access_token);
        localStorage.setItem('NCAgencyId', response.data.agencyId);
        if (response.data.agencyName) {
          localStorage.setItem('NCAgencyName', response.data.agencyName);
        }
        if (response.data.userId) {
          localStorage.setItem('NCUserId', response.data.userId);
        }
      }

      logLoginEvent(igToken);
      login(ncSetupUserName);
      setLoading(false);
      if (policy) {
        navigate(`/selectclient?policy=${encodeURIComponent(policy)}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    })
    .catch((error: any) => {
      setLoading(false);
      const status = error?.response?.status;

      if (status === 401) {
        clearAuthStorage();
        setError('Your session has expired. Please sign in again.');
      } else if (status === 409) {
        setError('You are not registered with Nowcerts. Please register first.');
      } else {
        setError('Unknown error while verifying Nowcerts credentials.');
      }
    });
}

function logLoginEvent(igToken: string) {
  try {
    const ncToken = localStorage.getItem('NCToken') || '';
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/log-login`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${igToken}`,
        'NCToken': ncToken,
      },
    }).catch(() => { /* fire-and-forget — silently ignore */ });
  } catch { /* never break login flow */ }
}

function decodePolicyParam(raw: string): string {
  if (!raw) return raw;
  try {
    const decoded = atob(raw);
    if (/^[\x20-\x7E]+$/.test(decoded)) return decoded;
  } catch {}
  return raw;
}

function clearAuthStorage() {
  try {
    localStorage.removeItem('igtoken');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('userName');
    localStorage.removeItem('RoleId');
    localStorage.removeItem('AvatarName');
    localStorage.removeItem('NowcertsAgentId');
    localStorage.removeItem('NCToken');
    localStorage.removeItem('NCAgencyId');
    localStorage.removeItem('NCUserId');
    localStorage.removeItem('SocialLogin');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
  } catch {
  }
}
